import type { PublicOpeningHoursDto } from '@/api/generated/model';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE BRANCH WIRE SHAPES — until the integrator re-emits `openapi.json`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `backend/openapi.json` is committed and is what the generated client reads.
 * The branch routes (`/branches*`, `PUT /staff/:id/branches`) and the
 * `branchId` / `branchName` fields every branch-aware response gains are built
 * by the backend streams of the same build, and reach that snapshot only at
 * the integrator's checkpoint C1. Until then this file is the ONE place their
 * shapes are written down — named exactly as the backend DTOs name them
 * (`backend/src/branches/dto/branch-response.dto.ts`), so the swap to
 * `@/api/generated/model` after C1 is an import change, not a rewrite.
 *
 * Nothing else in the panel declares a branch type. Screens import from here.
 */

/* -------------------------------------------------------------------------- */
/* Responses                                                                  */
/* -------------------------------------------------------------------------- */

/** Why «بایگانی» is blocked for a branch, if it is — the panel words the reason. */
export interface BranchArchiveBlock {
  code: 'PRIMARY' | 'FUTURE_APPOINTMENTS' | 'STOCK';
  /** How many future appointments (FUTURE_APPOINTMENTS only). */
  count: number | null;
}

/** `GET /branches`, `GET /branches/:id` and every branch write. */
export interface BranchResponse {
  id: string;
  name: string;
  /** The branch page's URL segment under the clinic page. */
  slug: string;
  isPrimary: boolean;
  addressLine: string | null;
  postalCode: string | null;
  provinceId: string | null;
  provinceName: string | null;
  cityId: string | null;
  cityName: string | null;
  districtId: string | null;
  districtName: string | null;
  lat: number | null;
  lng: number | null;
  phones: string[];
  googleMapsUrl: string | null;
  baladUrl: string | null;
  neshanUrl: string | null;
  workingHours: PublicOpeningHoursDto[] | null;
  /** For the primary this IS the clinic's switch (`followsClinicSwitches`). */
  directoryVisible: boolean;
  acceptsOnlineBooking: boolean;
  /** True for the primary: the two switches above are the clinic's, read-only here. */
  followsClinicSwitches: boolean;
  sortOrder: number;
  archivedAt: string | null;
  publicUpdatedAt: string | null;
  /** The public page (the clinic page for the primary). */
  publicUrl: string | null;
  /** Active staff who work at this branch. */
  staffCount: number;
  archiveBlock: BranchArchiveBlock | null;
  createdAt: string;
  updatedAt: string;
}

export interface MyBranch {
  id: string;
  name: string;
  isPrimary: boolean;
}

/** `GET /branches/mine` — what the switcher and every «شعبه» picker offer. */
export interface MyBranchesResponse {
  /** Live branches this caller sees, primary first (every branch for view_all). */
  branches: MyBranch[];
  /** «همهٔ شعبه‌ها» is allowed (branch.view_all, or a single-branch clinic). */
  canViewAll: boolean;
}

/** `GET /branches/usage` — «{used} از {limit} شعبه». −1 = unlimited. */
export interface BranchUsageResponse {
  used: number;
  limit: number;
  remaining: number;
  canAdd: boolean;
  /** The plan has the (derived) `branches` feature at all. */
  featureAllowed: boolean;
  planName: string | null;
  upgradePlanKey: string | null;
}

export interface BranchSlugAvailability {
  slug: string;
  available: boolean;
  reason: 'INVALID' | 'TAKEN' | null;
  suggestion: string | null;
}

/** A staff member's branch, as the staff presenter lists it (primary first). */
export interface StaffBranchRef {
  id: string;
  name: string;
  isPrimary: boolean;
}

/** `PUT /staff/:id/branches` */
export interface StaffBranchesResponse {
  staffId: string;
  branches: StaffBranchRef[];
}

/* -------------------------------------------------------------------------- */
/* Requests                                                                   */
/* -------------------------------------------------------------------------- */

export interface BranchFieldsInput {
  addressLine?: string | null;
  postalCode?: string | null;
  provinceId?: string | null;
  cityId?: string | null;
  districtId?: string | null;
  lat?: number | null;
  lng?: number | null;
  phones?: string[];
  googleMapsUrl?: string | null;
  baladUrl?: string | null;
  neshanUrl?: string | null;
  workingHours?: PublicOpeningHoursDto[] | null;
  /** Non-primary branches only; the primary follows the clinic's switches. */
  directoryVisible?: boolean;
  /** Non-primary branches only. */
  acceptsOnlineBooking?: boolean;
  sortOrder?: number;
}

export interface CreateBranchInput extends BranchFieldsInput {
  name: string;
  /** Lower-case Latin, digits and single hyphens; generated when omitted. */
  slug?: string;
  /** Staff who work here from day one — the add flow's last step. */
  staffIds?: string[];
}

export interface UpdateBranchInput extends BranchFieldsInput {
  name?: string;
  slug?: string;
}

/** The slug rule, mirrored from `BRANCH_SLUG_RE` in the backend DTO. */
export const BRANCH_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const BRANCH_SLUG_MIN_LENGTH = 2;
export const BRANCH_SLUG_MAX_LENGTH = 40;
export const BRANCH_NAME_MAX_LENGTH = 80;

/* -------------------------------------------------------------------------- */
/* Branch fields on existing responses                                        */
/* -------------------------------------------------------------------------- */

/**
 * What a branch-aware row carries after C1: appointments, sessions, lots,
 * payments, rooms, shifts, stock rows, timeline items. Read through
 * {@link branchOf} so a row from the stale generated type (no such keys) and
 * one from the re-emitted type read the same way.
 */
export interface BranchTagged {
  branchId?: string | null;
  branchName?: string | null;
}

export type WithBranch<T> = T & BranchTagged;

/** The branch a row names, or nulls when it names none. */
export function branchOf(row: object | null | undefined): {
  id: string | null;
  name: string | null;
} {
  const tagged = (row ?? {}) as BranchTagged;
  const id = typeof tagged.branchId === 'string' && tagged.branchId !== '' ? tagged.branchId : null;
  const name =
    typeof tagged.branchName === 'string' && tagged.branchName.trim() !== ''
      ? tagged.branchName
      : null;
  return { id, name };
}

/**
 * Whether rows of a history name branches worth showing: the viewer works
 * across several, or the rows span more than one, or a row happened at a
 * branch outside the viewer's own (a restricted doctor reading a visit at
 * another site — the patient file is one file, owner round 5). A
 * single-branch clinic never shows a branch.
 */
export function showsBranches(rows: readonly object[], myBranchIds: readonly string[]): boolean {
  if (myBranchIds.length > 1) return true;
  const ids = new Set(
    rows.map((row) => branchOf(row).id).filter((id): id is string => id !== null),
  );
  if (ids.size > 1) return true;
  return myBranchIds.length > 0 && [...ids].some((id) => !myBranchIds.includes(id));
}

/** A calendar doctor's branches (`CalendarDoctorResponse.branchIds` after C1). */
export function doctorBranchIds(doctor: object): string[] {
  const ids = (doctor as { branchIds?: unknown }).branchIds;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

/**
 * The `branchId` request parameter: present only when a branch is chosen.
 * Spread into a generated request's params or body — the intersection type is
 * assignable to the generated parameter type before C1 and identical after.
 */
export function branchParam(branchId: string | null | undefined): { branchId?: string } {
  return branchId ? { branchId } : {};
}
